import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Typography } from '@mui/material';
import styles from './PathField.module.scss';
import {
  formatCoverage,
  normalizePath,
  pathIsList,
  pathLeafName,
  resolvePathAgainstFields,
} from './kgUtils';

/**
 * One column picker, used by every path in the mapping config — record_key,
 * title, text[].path, dates[].path and facets[].path.
 *
 * It exists so the author never has to think about backticks. Column names with
 * a space, hyphen or dot must be quoted, and the consequence of getting it wrong
 * is invisible: an unquoted `tbl.col` is read as "field tbl, then field col" and
 * silently matches nothing, which only shows up as 0% coverage much later.
 *
 * So: picking from the list always produces a correctly quoted path, and typing
 * is resolved against the real column list on blur — with the result shown, so a
 * backtick appearing in the value reads as deliberate rather than as a bug.
 */
const PathField = ({
  value,
  onChange,
  fields = [],
  label = 'Column',
  placeholder = 'Pick or type a column',
  helperText,
  error,
  disabled = false,
  excludeLists = false,
  // Suppress the "set cardinality to many" nudge when the caller has already
  // done so — an instruction that stays on screen after you follow it reads as
  // an unresolved warning.
  showListHint = true,
  // Whether `fields` came from a real /inspect of loaded records. When it did
  // not, the list is only what the saved config already references, so the
  // "not found in the sample" warning would be claiming something unknown.
  hasSample = fields.length > 0,
  size = 'small',
}) => {
  const [quotedHint, setQuotedHint] = useState(null);

  const options = useMemo(() => {
    const usable = excludeLists ? fields.filter((f) => !f.isList && !f.multiValued) : fields;
    return usable.map((f) => f.path);
  }, [fields, excludeLists]);

  const fieldByPath = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.path, f])),
    [fields]
  );

  const commit = (raw) => {
    const resolved = resolvePathAgainstFields(raw, fields);
    if (resolved && resolved !== String(raw ?? '').trim()) {
      setQuotedHint(resolved);
    } else {
      setQuotedHint(null);
    }
    onChange(resolved);
  };

  const isList = value ? pathIsList(value) : false;

  return (
    <div className={styles.wrap}>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={options}
        value={value || ''}
        size={size}
        onChange={(_, next) => commit(next || '')}
        onInputChange={(_, next, reason) => {
          // Typing is left alone until it settles; blur/select does the quoting,
          // so the field doesn't fight the user mid-word.
          if (reason === 'clear') commit('');
        }}
        renderOption={(props, option) => {
          const field = fieldByPath[option];
          return (
            <li {...props} key={option}>
              <span className={styles.optionRow}>
                <span className={styles.optionName}>{pathLeafName(option)}</span>
                {field && (
                  <span className={styles.optionMeta}>
                    {field.needsQuoting && <em className={styles.optionQuoted}>quoted</em>}
                    {field.multiValued || field.isList ? (
                      <em className={styles.optionMulti}>many</em>
                    ) : null}
                    {/* Only when it is actually known. Config-derived options
                        carry no coverage, and a bare — in its place reads as a
                        measured value of nothing rather than as "not measured". */}
                    {Number.isFinite(Number(field.coveragePct)) &&
                      formatCoverage(field.coveragePct)}
                  </span>
                )}
              </span>
              {field && field.depth > 1 && <code className={styles.optionPath}>{option}</code>}
            </li>
          );
        }}
        noOptionsText={
          hasSample ? 'No matching column' : 'No columns yet — load a sample file'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={Boolean(error)}
            helperText={error || helperText}
            onBlur={(e) => commit(e.target.value)}
            // The placeholder is always present, so without this the floating
            // label sits on top of it and both read as one struck-through word.
            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            InputProps={{ ...params.InputProps, className: styles.monoInput }}
          />
        )}
      />

      {/* The value is stored quoted; say why, once, right where it happened. */}
      {quotedHint && (
        <Typography variant="caption" className={styles.quotedHint}>
          Saved as <code>{quotedHint}</code> — backticks keep this as one column name, so the
          spaces or dots in it aren’t read as nested fields.
        </Typography>
      )}

      {isList && !excludeLists && showListHint && (
        <Typography variant="caption" className={styles.listHint}>
          This column holds several values per record — set cardinality to “many”.
        </Typography>
      )}

      {/* A path that resolves to nothing in the sample is the 0%-coverage
          failure, caught here instead of after a full load. Only claimed when a
          sample was actually inspected — otherwise there is nothing to be
          absent from. */}
      {value && hasSample && fields.length > 0 && !fieldByPath[normalizePath(value)] && (
        <Typography variant="caption" className={styles.unknownHint}>
          Not found in the loaded sample — double-check the spelling, or ignore this if the
          column only appears in the full export.
        </Typography>
      )}

      {/* Without this, an empty picker on a reopened dataset reads as "this
          dataset has no columns" rather than "no sample is loaded yet". */}
      {!hasSample && !disabled && (
        <Typography variant="caption" className={styles.listHint}>
          {fields.length > 0
            ? 'Only columns already used by this mapping are listed — load a sample file for the full list.'
            : 'Load a sample file to list your columns. You can still type a column name.'}
        </Typography>
      )}
    </div>
  );
};

export default PathField;
